import { AnimatePresence } from 'framer-motion'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { Shell } from './components/Shell'
import { useAppearance, useMe } from './lib/session'
import { AdminPage } from './routes/Admin'
import {
  ConfirmEmailChangePage,
  ForgotPasswordPage,
  InvitationPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from './routes/Auth'
import { HomePage } from './routes/Home'
import { LandingPage } from './routes/Landing'
import { MePage } from './routes/Me'
import { SwapsPage } from './routes/Swaps'
import { PlanPage } from './routes/Plan'
import { ProfilePage } from './routes/Profile'
import { TeamPage } from './routes/Team'
import { TenantPage } from './routes/Tenant'
import { Banner, Spinner } from './ui'

/** Seiten, die auch ohne Anmeldung erreichbar sind. */
const OPEN_PATHS = [
  '/anmelden',
  '/registrieren',
  '/verifizieren',
  '/passwort-vergessen',
  '/passwort-neu',
  '/einladung',
]

export function App() {
  const location = useLocation()
  const me = useMe()

  // Farbschema aus der Betriebsfarbe. Ein Hook, eine Stelle — sonst nehmen
  // Betriebswechsel, Themawechsel und Systemwechsel drei verschiedene Wege.
  useAppearance(me.data?.active_tenant?.primary_color)

  if (me.isPending) return <Spinner label="Einen Moment …" />

  if (me.isError) {
    return (
      <div className="auth">
        <div className="auth__panel">
          <Banner tone="error">
            Der Server antwortet nicht. Bitte lade die Seite in einem Moment neu.
          </Banner>
        </div>
      </div>
    )
  }

  const user = me.data
  // `startsWith` würde bei '/' auf **jeden** Pfad passen. Die Startseite ist
  // deshalb ein Sonderfall: genau '/', nichts darunter.
  const isOpenPath =
    location.pathname === '/' || OPEN_PATHS.some((path) => location.pathname.startsWith(path))

  if (!user && !isOpenPath) {
    return <Navigate to="/anmelden" replace state={{ from: location.pathname }} />
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Offene Seiten */}
        {/* Die Startseite gibt es nur für Nichtangemeldete. Wer ein Konto hat,
            bekommt unter '/' die Übersicht — niemand will nach dem Anmelden
            erst an einer Verkaufsseite vorbei. */}
        {!user && <Route path="/" element={<LandingPage />} />}
        <Route
          path="/anmelden"
          element={user ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/registrieren"
          element={user ? <Navigate to="/" replace /> : <RegisterPage />}
        />
        <Route path="/verifizieren" element={<VerifyEmailPage />} />
        <Route path="/e-mail-bestaetigen" element={<ConfirmEmailChangePage />} />
        <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
        <Route path="/passwort-neu" element={<ResetPasswordPage />} />
        <Route path="/einladung" element={<InvitationPage me={user ?? null} />} />

        {/* Angemeldeter Bereich */}
        {user && (
          <Route element={<Shell me={user} />}>
            <Route path="/" element={<HomePage me={user} />} />
            <Route path="/meine-schichten" element={<MePage />} />
            <Route path="/tausch" element={<SwapsPage me={user} />} />
            <Route path="/plan" element={<PlanPage me={user} />} />
            <Route path="/team" element={<TeamPage me={user} />} />
            <Route path="/betrieb" element={<TenantPage me={user} />} />
            <Route path="/profil" element={<ProfilePage me={user} />} />
            <Route
              path="/verwaltung"
              element={user.is_superadmin ? <AdminPage me={user} /> : <Navigate to="/" replace />}
            />
          </Route>
        )}

        {/* Unbekannter Pfad: nach Hause. Für Angemeldete ist das die
            Übersicht, für alle anderen die Startseite. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
  )
}
